import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/api";
import StatCard from "@/components/StatCard";
import LiveTicker from "@/components/LiveTicker";
import ChallengeCard from "@/components/ChallengeCard";
import TargetIndicator from "@/components/TargetIndicator";
import TargetTicker from "@/components/TargetTicker";
import CrowningPlatform from "@/components/CrowningPlatform";
import { GlassCard } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { useAuth } from "@/context/AuthContext";
import { PHRASES } from "@/lib/constants";
import {
  HandHeart, CheckCircle2, Users, GraduationCap, Briefcase, UserCheck,
  Clock, Coins,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const chartTheme = {
  grid: "rgba(180,120,20,0.18)",
  axis: "rgba(68,64,60,0.75)",
};

export default function CoordinatorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [phrase] = useState(PHRASES[Math.floor(Math.random() * PHRASES.length)]);

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-stone-500">جارٍ التحميل...</div>;

  const cards = [
    { icon: HandHeart, label: "إجمالي الفرص", value: stats.total_opportunities, accent: "#0EA5E9" },
    { icon: CheckCircle2, label: "الفرص المنفذة", value: stats.done_opportunities, accent: "#10B981" },
    { icon: GraduationCap, label: "إجمالي عدد الطلبة", value: stats.students_total, accent: "#0284C7" },
    { icon: Users, label: "عدد الطلبة المتطوعين", value: stats.student_volunteers, accent: "#38BDF8" },
    { icon: Briefcase, label: "إجمالي عدد منسوبي المدرسة", value: stats.staff_total, accent: "#B45309" },
    { icon: UserCheck, label: "عدد المنسوبين المتطوعين", value: stats.staff_volunteers, accent: "#D4AF37" },
    { icon: Clock, label: "إجمالي الساعات", value: stats.total_hours, accent: "#0EA5E9" },
    { icon: Coins, label: "العائد الاقتصادي", value: stats.economic_value, suffix: "ر.س", accent: "#D4AF37" },
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="glass rounded-2xl p-6 relative overflow-hidden"
      >
        <div className="absolute -left-10 -top-10 w-40 h-40 rounded-full bg-amber-500/10 blur-2xl" />
        <p className="text-sm text-stone-500">لوحة تحكم المدرسة</p>
        <h2 className="text-2xl sm:text-3xl font-head font-bold gold-text mt-1">{phrase}</h2>
      </motion.div>

      <InspireBanner />

      <TargetTicker />

      <LiveTicker />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <StatCard key={c.label} {...c} testId={`stat-${i}`} delay={i * 0.04} />
        ))}
      </div>

      <TargetIndicator rate={stats.participation_rate} target={stats.target || 90} testId="coordinator-target"
        studentsTotal={stats.students_total} studentVols={stats.student_volunteers}
        subtitle={stats.students_total > 0 ? `عدد الطلبة المتطوعين ${stats.student_volunteers} من إجمالي ${stats.students_total} طالب` : "يُدخل مدير القسم إجمالي عدد طلاب المدرسة لاحتساب النسبة"} />

      <CrowningPlatform highlightSchoolId={user?.school_id} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <GlassCard className="lg:col-span-2">
          <h3 className="font-head font-bold text-lg text-stone-800 mb-4">المخطط الشهري</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
              <XAxis dataKey="month" stroke={chartTheme.axis} fontSize={12} />
              <YAxis stroke={chartTheme.axis} fontSize={12} />
              <Tooltip cursor={{ fill: "rgba(180,120,20,0.07)" }} contentStyle={{ background: "#241710", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#FDF8F0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="opportunities" name="الفرص" fill="#D4AF37" radius={[6, 6, 0, 0]} />
              <Bar dataKey="volunteers" name="المتطوعون" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <div className="space-y-6">
          <ChallengeCard isCoordinator />
        </div>
      </div>
    </div>
  );
}
